/**
 * Customer Portal Knowledge Base Article Page
 * View a single help article
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import CustomerPortalLayout from '@/components/customer-portal/CustomerPortalLayout';
import { customerPortalService, type KBArticle } from '@/services/api/customerPortal';
import {
  ArrowLeft,
  Clock,
  Eye,
  ThumbsUp,
  ThumbsDown,
  BookOpen,
  AlertCircle,
  CheckCircle,
  Ticket,
} from 'lucide-react';

export default function CustomerPortalArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<KBArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackType, setFeedbackType] = useState<'helpful' | 'not_helpful' | null>(null);

  useEffect(() => {
    if (slug) {
      loadArticle();
    }
  }, [slug]);

  const loadArticle = async () => {
    if (!slug) return;
    try {
      const data = await customerPortalService.getArticle(slug);
      setArticle(data);
    } catch (error) {
      console.error('Failed to load article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (isHelpful: boolean) => {
    if (!article || feedbackSubmitted) return;

    setFeedbackType(isHelpful ? 'helpful' : 'not_helpful');

    if (!isHelpful) {
      setShowFeedbackForm(true);
    } else {
      await submitFeedback(isHelpful);
    }
  };

  const submitFeedback = async (isHelpful: boolean, comment?: string) => {
    if (!article) return;

    try {
      await customerPortalService.submitArticleFeedback(article.id, isHelpful, comment);
      setFeedbackSubmitted(true);
      setShowFeedbackForm(false);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  if (loading) {
    return (
      <CustomerPortalLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </CustomerPortalLayout>
    );
  }

  if (!article) {
    return (
      <CustomerPortalLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Article Not Found</h2>
          <p className="text-gray-500 mb-4">The article you're looking for doesn't exist.</p>
          <Link to="/customer-portal/knowledge-base">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Knowledge Base
            </Button>
          </Link>
        </div>
      </CustomerPortalLayout>
    );
  }

  return (
    <CustomerPortalLayout>
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            to="/customer-portal/knowledge-base"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Knowledge Base
          </Link>
        </div>

        {/* Article Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-3">
            {article.categoryName && (
              <Badge variant="secondary">{article.categoryName}</Badge>
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              Updated {new Date(article.publishedAt).toLocaleDateString()}
            </span>
            <span className="flex items-center">
              <Eye className="w-4 h-4 mr-1" />
              {article.viewCount} views
            </span>
            <span className="flex items-center">
              <ThumbsUp className="w-4 h-4 mr-1" />
              {article.helpfulCount} found helpful
            </span>
          </div>
        </div>

        {/* Article Content */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div
              className="prose dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: article.body }}
            />
          </CardContent>
        </Card>

        {/* Feedback Section */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            {feedbackSubmitted ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                  Thank you for your feedback!
                </h4>
                <p className="text-sm text-gray-500">
                  Your input helps us improve our knowledge base.
                </p>
              </div>
            ) : showFeedbackForm ? (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  How can we improve this article?
                </h4>
                <Textarea
                  placeholder="Tell us what was missing or unclear..."
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  rows={3}
                />
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={() => submitFeedback(false, feedbackComment)}
                    className="bg-v2accent hover:bg-v2accent/90"
                  >
                    Submit Feedback
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowFeedbackForm(false);
                      setFeedbackType(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                  Was this article helpful?
                </h4>
                <div className="flex items-center justify-center space-x-4">
                  <Button
                    variant="outline"
                    className={`${
                      feedbackType === 'helpful'
                        ? 'border-green-500 text-green-600'
                        : ''
                    }`}
                    onClick={() => handleFeedback(true)}
                  >
                    <ThumbsUp className="w-5 h-5 mr-2" />
                    Yes, helpful
                  </Button>
                  <Button
                    variant="outline"
                    className={`${
                      feedbackType === 'not_helpful'
                        ? 'border-red-500 text-red-600'
                        : ''
                    }`}
                    onClick={() => handleFeedback(false)}
                  >
                    <ThumbsDown className="w-5 h-5 mr-2" />
                    No, not helpful
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Still Need Help */}
        <Card className="bg-gray-50 dark:bg-gray-800/50 border-dashed">
          <CardContent className="pt-6 text-center">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Still need help?
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              If this article didn't answer your question, our support team is ready to help.
            </p>
            <Link to="/customer-portal/tickets/new">
              <Button>
                <Ticket className="w-4 h-4 mr-2" />
                Submit a Ticket
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </CustomerPortalLayout>
  );
}
